import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { useAuth } from '../contexts/AuthContext';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerRetina,
  shadowUrl: markerShadow,
});

interface DeviceOption {
  id: string;
  name: string;
}

interface DeviceSettingDto {
  id: string;
  settingName: string;
  settingValue: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  defaultCenter: [number, number];
  onSaved: () => void;
}

const ClickToPlace: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const SetDeviceLocationModal: React.FC<Props> = ({ open, onClose, businessId, businessName, defaultCenter, onSaved }) => {
  const auth = useAuth();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };

  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [point, setPoint] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedDeviceId('');
    setPoint(null);
    setError(null);
    setLoading(true);
    fetch(`/api/devices?businessId=${businessId}&topLevelOnly=true`, { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: DeviceOption[]) => setDevices(data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessId]);

  const upsertSetting = async (deviceId: string, settingName: string, settingValue: string) => {
    const existingRes = await fetch(`/api/devices/${deviceId}/settings`, { headers: authHeader });
    const existing: DeviceSettingDto[] = existingRes.ok ? await existingRes.json() : [];
    const match = existing.find((s) => s.settingName === settingName);
    if (match) {
      await fetch(`/api/devices/${deviceId}/settings/${match.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: match.id, deviceId, settingName, settingValue }),
      });
    } else {
      await fetch(`/api/devices/${deviceId}/settings`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ settingName, settingValue }),
      });
    }
  };

  const handleSave = async () => {
    if (!selectedDeviceId || !point) return;
    setSaving(true);
    setError(null);
    try {
      await upsertSetting(selectedDeviceId, 'device.latitude', String(point[0]));
      await upsertSetting(selectedDeviceId, 'device.longitude', String(point[1]));
      onSaved();
      onClose();
    } catch {
      setError('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Set Device Location — {businessName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth sx={{ mb: 2, mt: 1 }} disabled={loading}>
          <InputLabel id="location-device-label">Device</InputLabel>
          <Select
            labelId="location-device-label"
            value={selectedDeviceId}
            label="Device"
            onChange={(e) => setSelectedDeviceId(e.target.value)}
          >
            {devices.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Click the map to drop a pin at the device's location.
        </Typography>
        <Box sx={{ height: 400, borderRadius: 1, overflow: 'hidden' }}>
          <MapContainer center={defaultCenter} zoom={6} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickToPlace onPick={(lat, lng) => setPoint([lat, lng])} />
            {point && <Marker position={point} />}
          </MapContainer>
        </Box>
        {point && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {point[0].toFixed(6)}, {point[1].toFixed(6)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!selectedDeviceId || !point || saving}>
          {saving ? 'Saving…' : 'Save Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetDeviceLocationModal;
